import React, { useRef, useEffect, useState, useCallback } from 'react';

interface WebcamCaptureProps {
  onCapture: (file: File) => void;
  autoStart?: boolean;
  onModalClose?: () => void;
}

const WebcamCapture: React.FC<WebcamCaptureProps> = ({
  onCapture,
  autoStart = true,
  onModalClose,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
        console.log('Camera track stopped:', track.kind);
      });
      setStream(null);
      setIsStreaming(false);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  }, [stream]);

  const startCamera = useCallback(async () => {
    if (isStreaming || stream) return; // Prevent double start

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 380 },
          facingMode: 'user',
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        setIsStreaming(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  }, [isStreaming, stream]);

  useEffect(() => {
    if (autoStart) {
      startCamera();
    }

    return () => {
      stopCamera();
    };
  }, [autoStart, startCamera, stopCamera]);

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        console.error('2D context not available');
        return;
      }

      context.drawImage(video, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'face-capture.jpg', {
            type: 'image/jpeg',
          });
          stopCamera(); // Stop camera after capture
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
          onCapture(file);
        }
      }, 'image/jpeg', 0.8);
    }
  };

  // const handleModalClose = () => {
  //   stopCamera();
  //   if (onModalClose) onModalClose();
  // };

  return (
    <div className="webcam-capture">
      <div className="camera-container" style={{ position: 'relative' }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            maxWidth: '500px',
            height: 'auto',
            borderRadius: '8px',
            backgroundColor: '#000',
          }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {!isStreaming && !autoStart && (
          <div
            className="camera-placeholder"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.7)',
              padding: '20px',
              borderRadius: '8px',
            }}
          >
            <i className="fas fa-camera fa-3x mb-3"></i>
            <p>Camera not started</p>
          </div>
        )}
      </div>

      <div className="camera-controls mt-3">
        {!isStreaming && !autoStart && (
          <button
            type="button"
            className="btn btn-primary me-2"
            onClick={startCamera}
          >
            <i className="fas fa-video me-2"></i>
            Start Camera
          </button>
        )}

        {isStreaming && (
          <>
            <button
              type="button"
              className="btn btn-success me-2"
              onClick={captureImage}
            >
              <i className="fas fa-camera me-2"></i>
              Capture
            </button>
            {/* <button
              type="button"
              className="btn btn-secondary"
              onClick={handleModalClose}
            >
              <i className="fas fa-stop me-2"></i>
              Stop Camera
            </button> */}
          </>
        )}
      </div>
    </div>
  );
};

export default WebcamCapture;
